import isEqual from 'lodash-es/isEqual';
import { nextTick } from 'vue';

const viewableKey = Symbol('v-viewable');

type ViewableCallback = (result: boolean, entry: IntersectionObserverEntry) => void;

class ViewableState {
  private observer: IntersectionObserver | null;
  private el: Element;
  private frozen: boolean;
  private options: {
    callback: ViewableCallback;
    intersection: IntersectionObserverInit;
  };
  private oldResult: undefined | boolean;
  private callback: ViewableCallback | null;
  private modifiers?: {
    once?: boolean;
  };

  constructor(el, options, modifiers = {}) {
    this.el = el;
    this.modifiers = modifiers;
    this.observer = null;
    this.callback = null;
    this.frozen = false;
    this.options = {
      callback: () => {},
      intersection: {},
    };
    this.createObserver(options);
  }

  /**
   * Default threshold of 100%.
   * Informs when the element is 100% visibile, or at the provided threshold(s)
   * Does not support an array of values.
   * @see: https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#thresholds
   */
  get threshold() {
    return this.options.intersection && typeof this.options.intersection.threshold === 'number'
      ? this.options.intersection.threshold
      : 1;
  }

  get intersectionObserverOptions() {
    return {
      threshold: this.threshold,
      ...this.options.intersection,
    };
  }

  createObserver(options) {
    if (this.observer) {
      this.destroyObserver();
    }

    if (this.frozen) {
      return;
    }

    this.options = this.processOptions(options);

    this.callback = (result, entry) => {
      this.options.callback(result, entry);

      if (result && this.modifiers?.once) {
        this.frozen = true;
        this.destroyObserver();
      }
    };

    this.oldResult = undefined;

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        let entry = entries[0];

        if (entries.length > 1) {
          const intersectingEntry = entries.find((e) => e.isIntersecting);

          if (intersectingEntry) {
            entry = intersectingEntry;
          }
        }

        if (this.callback) {
          // Use isIntersecting if possible because browsers can report isIntersecting as true, but intersectionRatio as 0, when something very slowly enters the viewport.
          const result = entry.isIntersecting && entry.intersectionRatio >= this.threshold;

          if (result === this.oldResult) {
            return;
          }

          this.oldResult = result;
          this.callback(result, entry);
        }
      }, this.intersectionObserverOptions);
    }

    // Wait for the element to be in document
    nextTick(() => {
      if (this.observer) {
        this.observer.observe(this.el);
      }
    });
  }

  destroyObserver() {
    if (this.observer) {
      this.observer.unobserve(this.el);
      this.observer.disconnect();
      this.observer = null;
    }
  }

  private processOptions(value) {
    const options =
      typeof value === 'function'
        ? { callback: value } // Simple options (callback-only)
        : value; // Options object

    return options;
  }
}

function mount(el, { modifiers = {}, value }) {
  if (!value) {
    return;
  }

  if (value.intersection?.threshold instanceof Array) {
    throw new Error('`threshold` must be a number. v-viewable does not support `threshold` as an array.');
  }

  const state = new ViewableState(el, value, modifiers);

  el[viewableKey] = state;
}

function updated(el, { modifiers, value, oldValue }) {
  if (isEqual(value, oldValue) || value.toString() === oldValue.toString()) {
    return;
  }

  const state = el[viewableKey];

  if (!value) {
    unmount(el);
    return;
  }

  if (state) {
    state.createObserver(value);
  } else {
    mount(el, { modifiers, value });
  }
}

function unmount(el) {
  const state = el[viewableKey];

  if (state) {
    state.destroyObserver();
    delete el[viewableKey];
  }
}

export default {
  beforeMount: mount,
  updated,
  unmounted: unmount,
};
